import type { MetaFunction } from '@remix-run/node';
import { Link } from '@remix-run/react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { post } from '~/lib/api';

export const meta: MetaFunction = () => [{ title: 'Forgot Password — Deleg8 Studio' }];

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await post<{ message: string }>(
        '/auth/forgot-password',
        { email },
      );
      setSent(true);
      toast.success('Check your email for reset instructions');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-surface-900 via-creative-950 to-surface-900 px-4">
      <div className="pointer-events-none absolute -left-40 -top-40 h-[600px] w-[600px] rounded-full bg-gradient-to-br from-creative-100/40 to-accent-100/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-[500px] w-[500px] rounded-full bg-gradient-to-tl from-heat-100/30 to-creative-100/20 blur-3xl" />

      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-glow p-8">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-gradient-brand">
          Deleg8 Studio
        </p>

        {!sent ? (
          <form onSubmit={handleSubmit}>
            <h1 className="mt-2 text-2xl font-bold text-white">Reset your password</h1>
            <p className="mt-1 text-sm text-white/60">
              Enter your email and we&apos;ll send you instructions to reset your password.
            </p>

            <label className="mt-6 flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-white/80">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 px-3 py-2 text-sm shadow-sm outline-none transition focus:border-creative-400 focus:ring-2 focus:ring-creative-400/30"
              />
            </label>

            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full rounded-xl border-0 bg-gradient-to-r from-creative-600 via-accent-500 to-heat-500 px-4 py-2.5 text-sm font-semibold text-white shadow-glow transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <p className="mt-4 text-center text-sm text-white/60">
              Remember your password?{' '}
              <Link to="/login" className="font-semibold text-creative-300 hover:text-creative-200">
                Sign in
              </Link>
            </p>
          </form>
        ) : (
          <div>
            <h1 className="mt-2 text-2xl font-bold text-white">Check your email</h1>
            <p className="mt-2 text-sm text-white/60">
              If an account exists for <span className="font-semibold text-white/80">{email}</span>,
              you&apos;ll receive password reset instructions.
            </p>

            <Link
              to="/login"
              className="mt-6 block w-full rounded-xl border border-white/20 bg-white/5 px-4 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-white/10"
            >
              Back to Sign in
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
